import React from 'react';
import type { ReactNode } from 'react';
import cx from 'classnames';
import {
  Card,
  Collapse,
  Tag,
  TagSizeEnum,
  TagVariantEnum,
  Typography,
  TypographyComponentEnum,
  TypographyVariant,
} from '@unisporkal/alliance-ui';
import styles from './ExpandableBuyCard.module.scss';

export type ExpandableBuyCardProps = {
  title: string;
  subtitle?: ReactNode;
  price?: string;
  priceLabel?: ReactNode;
  badge?: ReactNode;
  isExpanded: boolean;
  isSelected: boolean;
  onExpand: () => void;
  onSelect: () => void;
  children: ReactNode;
  'data-testid'?: string;
};

const ExpandableBuyCard = ({
  title,
  subtitle,
  price,
  priceLabel,
  badge,
  isExpanded,
  isSelected,
  onExpand,
  onSelect,
  children,
  'data-testid': dataTestId,
}: ExpandableBuyCardProps) => {
  const handleCardClick = () => {
    if (!isSelected) {
      onSelect();
    }

    if (!isExpanded) {
      onExpand();
    }
  };

  // The whole card is the click target; header button clicks (including
  // keyboard activation) bubble up to it, so the button needs no handler.
  return (
    <Card
      className={cx(styles.card, { [styles.selected]: isSelected })}
      onClick={handleCardClick}
      data-testid={dataTestId}
    >
      <button
        type="button"
        className={styles.header}
        aria-expanded={isExpanded}
      >
        <div className={styles.headerMain}>
          {badge && (
            <Tag
              variant={TagVariantEnum.PROMO}
              size={TagSizeEnum.ULTRA_COMPACT}
            >
              {badge}
            </Tag>
          )}
          <div className={styles.titleGroup}>
            <Typography
              variant={TypographyVariant.body.medium}
              component={TypographyComponentEnum.DIV}
            >
              <strong>{title}</strong>
            </Typography>
            {subtitle}
          </div>
        </div>
        {price && (
          <div className={styles.priceGroup}>
            <Typography
              variant={TypographyVariant.body.small}
              component={TypographyComponentEnum.SPAN}
              className={styles.price}
            >
              {price}
            </Typography>
            {priceLabel && (
              <Typography
                variant={TypographyVariant.body.xsmall}
                component={TypographyComponentEnum.SPAN}
                className={styles.priceLabel}
              >
                {priceLabel}
              </Typography>
            )}
          </div>
        )}
      </button>
      <Collapse in={isExpanded}>
        <div className={styles.body}>{children}</div>
      </Collapse>
    </Card>
  );
};

export default ExpandableBuyCard;
